import React, { useMemo } from "react";
import {
  AutocompleteRenderGetTagProps,
  Box,
  Checkbox,
  Chip,
  Typography,
} from "@mui/material";
import { ErrorMessage, FormikValues, useFormikContext } from "formik";
import FormDropdownField from "../../../../../common/Form/Dropdown/FormDropdownField";
import InputField from "../../../../../common/Form/Input/InputField";
import {
  ALL_STUDY_PHASE,
  SUBMISSION_TYPES,
  TYPES_OF_FUNDING,
  ALL_TYPE_OF_STUDY,
} from "../../../../../../constants";
import {
  useGetIrbSubmissionInfoLazyQuery,
  useGetProtocolListQuery,
  useGetSubmissionDropdownListQuery,
} from "../../../../../../generated/graphql";
import FormToggleButton from "../../../../../common/Form/ToggleButton/FormToggleButton";
import DropdownWithOther from "../../../IRBSubmission/CreateIRBSubmission/StudyDetails/DropdownWithOther";
import { Link, useParams } from "react-router-dom";
import {
  DEPARTMENT,
  EMAILS,
  FUND_TYPE,
  MICR,
  PRINCIPAL_INVESTIGATOR,
  PROTOCOL_NUMBER,
  PROTOCOL_NUMBER_OTHERS,
  SPONSOR,
  STUDY_PHASE,
  TITLE,
  TYPE_OF_STUDY,
} from "../../../../../../constants/forms";
import config from "../../../../../../utils/config";

type studyDetailsProps = {
  value?: number;
};

const StudyDetailsIRBAddSub = (props: studyDetailsProps): JSX.Element => {
  const { value } = props;
  let subType = "";
  switch (value) {
    case 0:
      subType = "INITIAL_REVIEW";
      break;
    case 1:
      subType = "ADDITIONAL_DOCUMENTS";
      break;
    case 2:
      subType = "EC_NOTIFICATION";
      break;
    case 3:
      subType = "EC_ANNUAL_REPORT";
      break;
    default:
      subType = "";
      break;
  }
  const { data, loading, error } = useGetProtocolListQuery({
    variables: {
      subType: subType,
      submissionType: SUBMISSION_TYPES.IRB,
    },
  });

  const protocolNumbers = data?.getProtocolList.map((item) => {
    return item.protocolNumber;
  });

  let id: string;
  const getIdFromValue = (number: string) => {
    data?.getProtocolList.forEach((item, i) => {
      if (item.protocolNumber === number) {
        id = item._id;
      }
    });
    return id;
  };
  const { values, setFieldValue, setValues } = useFormikContext<FormikValues>();
  const [getIrbById] = useGetIrbSubmissionInfoLazyQuery();
  const { data: dropdownLists } = useGetSubmissionDropdownListQuery({
    variables: {},
  });
  const dropdownInputHandler = (fieldName: string, value: any) => {
    setFieldValue(fieldName, value);
  };
  const dropdownInputHandlerPN = async (fieldName: string, value: any) => {
    const id = getIdFromValue(value);
    // api call
    const newValues = await getDataFromProtocolNumber(id);
    setValues({
      ...newValues,
      [fieldName]: value,
    });
    // setFieldValue(fieldName, value);
  };
  const checkboxInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, e.target.checked);
  };

  const toggleHandler = (e: React.MouseEvent, newToggle: boolean) => {
    setFieldValue("funding", newToggle);
    if (!newToggle) {
      setFieldValue("fundType", "");
    }
  };

  const getDataFromProtocolNumber = async (id: string) => {
    const { data } = await getIrbById({
      variables: { id },
    });
    if (data) {
      const {
        department,
        isFunded,
        fundType,
        principalInvestigator,
        sponsor,
        studyPhase,
        title,
        emails,
        typeOfStudy,
        micr,
      } = data.irb;
      return {
        ...values,
        department,
        isFunded,
        fundType,
        principalInvestigator,
        sponsor,
        studyPhase,
        title,
        typeOfStudy,
        emails,
        isTermsAccepted: true,
        micr,
      };
    }
    return values;
  };

  const isExistingProtocolNumber = !(
    values[PROTOCOL_NUMBER_OTHERS] === values[PROTOCOL_NUMBER]
  );

  // console.log(values);

  return (
    <>
      <DropdownWithOther
        label="1.1 Protocol Number/Short Title"
        placeholder="Protocol Number/Short Title"
        name={PROTOCOL_NUMBER}
        callBack={dropdownInputHandlerPN}
        options={[...(protocolNumbers || []), "Others"]} //make this dynamic
        disabled={false}
      />
      <InputField
        label={"1.2 MICR #"}
        name={MICR}
        placeholder={"Short Text"}
        value={values[MICR]}
        disabled={isExistingProtocolNumber}
      />
      <Box className="mg-b-50">
        <InputField
          label={"1.3 Study Title"}
          name={TITLE}
          placeholder={"Short Text"}
          value={values[TITLE]}
          disabled={isExistingProtocolNumber}
        />
      </Box>
      <Box className="mg-b-50">
        <DropdownWithOther
          options={[
            ...(dropdownLists?.getSubmissionDropdownList.sponsor || []),
            "Others",
          ]}
          label={"1.4 Sponsor"}
          name={SPONSOR}
          placeholder={"Short Text"}
          disabled={isExistingProtocolNumber}
        />
      </Box>
      <Box className="mg-b-50">
        <DropdownWithOther
          options={[
            ...(dropdownLists?.getSubmissionDropdownList
              .principalInvestigator || []),
            "Others",
          ]}
          label={"1.5 Principal Investigator"}
          name={PRINCIPAL_INVESTIGATOR}
          placeholder={"Short Text"}
          disabled={isExistingProtocolNumber}
        />
      </Box>
      <Box className="mg-b-50">
        <InputField
          label={"1.6 Department"}
          name={DEPARTMENT}
          placeholder={"Short Text"}
          value={values[DEPARTMENT]}
          disabled={isExistingProtocolNumber}
        />
      </Box>
      <Box className="mg-b-50">
        {isExistingProtocolNumber ? (
          <>
            <label className="form-label mg-b-20 font-size-14">
              1.7 Email (EC, CRC and PI)
            </label>
            <Typography variant={"body1"} component={"h3"} color={"secondary"}>
              {values?.emails?.join(", ")}
            </Typography>
          </>
        ) : (
          <FormDropdownField
            label="1.7 Email (EC, CRC and PI)"
            value={values[EMAILS]}
            name={EMAILS}
            options={["user1@gmail.com", "user2@gmail.com"]}
            inputHandler={dropdownInputHandler}
            multiple
            renderTags={(
              value: readonly string[],
              getTagProps: AutocompleteRenderGetTagProps
            ) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            // disabled={true}
          />
        )}
      </Box>
      <DropdownWithOther
        name={STUDY_PHASE}
        label={"1.8  Study Phase"}
        placeholder={"Study phase"}
        options={ALL_STUDY_PHASE}
        disabled={isExistingProtocolNumber}
      />
      <Box className="mg-b-50">
        <DropdownWithOther
          label={"1.9 Type Of Study"}
          name={TYPE_OF_STUDY}
          placeholder={"Type of Study"}
          options={ALL_TYPE_OF_STUDY}
          disabled={isExistingProtocolNumber}
        />
      </Box>
      <DropdownWithOther
        label="1.10 Funding / Grant"
        name={FUND_TYPE}
        placeholder={"Type of Funding"}
        options={TYPES_OF_FUNDING}
        disabled={isExistingProtocolNumber}
      />

      <Box className={"custom-checkbox mg-b-50"}>
        <Checkbox
          checked={values.isTermsAccepted}
          name="isTermsAccepted"
          onChange={checkboxInputHandler}
          //   disabled={!!id}
        />
        <Link to={config.terms} className="form-label font-size-14 font-weight-medium cursor-pointer text-danger">
          Terms and Conditions
        </Link>
        <ErrorMessage
          name="isTermsAccepted"
          render={(msg) => {
            return (
              <div>
                <span className="text-danger font-size-sm">{msg}</span>
              </div>
            );
          }}
        />
      </Box>
    </>
  );
};

export default StudyDetailsIRBAddSub;
